/**
 * Indian Market Utilities
 * SEBI / NSE / BSE compliant helpers for Indian retail traders
 */

// ─── INR Formatting ──────────────────────────────────────────────────────────

/** Format a number as Indian Rupees (₹) with en-IN locale */
export function formatINR(value: number, decimals = 2): string {
  return `₹${Math.abs(value).toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/** Format large INR values with suffix (L = Lakh, Cr = Crore) */
export function formatINRCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_00_00_000) return `${sign}₹${(abs / 1_00_00_000).toFixed(2)} Cr`;
  if (abs >= 1_00_000)    return `${sign}₹${(abs / 1_00_000).toFixed(2)} L`;
  if (abs >= 1_000)       return `${sign}₹${(abs / 1_000).toFixed(2)} K`;
  return `${sign}₹${abs.toFixed(2)}`;
}

/** Format number with Indian comma system (e.g. 1,23,456.78) */
export function formatIndianNumber(value: number, decimals = 2): string {
  return value.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// ─── IST Timezone ─────────────────────────────────────────────────────────────

/** Get current IST time string (HH:MM:SS IST) — safe for SSR (no Date in render) */
export function getISTTimeString(date: Date): string {
  // IST = UTC+5:30
  const utc = date.getTime() + date.getTimezoneOffset() * 60000;
  const ist = new Date(utc + 5.5 * 3600000);
  const hh = String(ist.getHours()).padStart(2, '0');
  const mm = String(ist.getMinutes()).padStart(2, '0');
  const ss = String(ist.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss} IST`;
}

/** Format a date in IST for display */
export function formatISTDate(date: Date): string {
  const utc = date.getTime() + date.getTimezoneOffset() * 60000;
  const ist = new Date(utc + 5.5 * 3600000);
  return ist.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── NSE Market Hours (IST) ───────────────────────────────────────────────────

export const NSE_MARKET_HOURS = {
  preOpen:    { start: '09:00', end: '09:07' },
  preOpenGap: { start: '09:07', end: '09:15' },
  regular:    { start: '09:15', end: '15:30' },
  postClose:  { start: '15:40', end: '16:00' },
  currency:   { start: '09:00', end: '17:00' },
  commodity:  { start: '09:00', end: '23:30' },
};

export type MarketSession = 'PRE_OPEN' | 'OPEN' | 'POST_CLOSE' | 'CLOSED';

/**
 * Returns the current Indian market session based on IST time.
 *
 * Session rules (NSE/BSE):
 *  - PRE_OPEN  : 09:00:00 – 09:07:59 IST (pre-market order collection)
 *  - OPEN      : 09:15:00 – 15:29:59 IST (live market)
 *  - POST_CLOSE: 15:40:00 – 15:59:59 IST (post-close session)
 *  - CLOSED    : all other times, weekends
 *
 * After 15:30 IST the last traded price (prevClose) is displayed for all indices.
 */
export function getMarketSession(date: Date): MarketSession {
  const utc = date.getTime() + date.getTimezoneOffset() * 60000;
  const ist = new Date(utc + 5.5 * 3600000);
  const day = ist.getDay(); // 0=Sun, 6=Sat
  if (day === 0 || day === 6) return 'CLOSED';
  const hh = ist.getHours();
  const mm = ist.getMinutes();
  const ss = ist.getSeconds();
  // Use total seconds from midnight for precise comparison
  const totalSec = hh * 3600 + mm * 60 + ss;
  const T_PRE_START  = 9 * 3600;           // 09:00:00
  const T_PRE_END    = 9 * 3600 + 7 * 60;  // 09:07:00  (exclusive end)
  const T_OPEN_START = 9 * 3600 + 15 * 60; // 09:15:00
  const T_OPEN_END   = 15 * 3600 + 30 * 60;// 15:30:00  (exclusive end)
  const T_POST_START = 15 * 3600 + 40 * 60;// 15:40:00
  const T_POST_END   = 16 * 3600;           // 16:00:00  (exclusive end)

  if (totalSec >= T_PRE_START  && totalSec < T_PRE_END)    return 'PRE_OPEN';
  if (totalSec >= T_OPEN_START && totalSec < T_OPEN_END)   return 'OPEN';
  if (totalSec >= T_POST_START && totalSec < T_POST_END)   return 'POST_CLOSE';
  return 'CLOSED';
}

/**
 * Returns true if the market is currently in live trading session (9:15 – 15:29:59 IST).
 */
export function isMarketLive(date: Date): boolean {
  return getMarketSession(date) === 'OPEN';
}

/**
 * Returns true if the market is in pre-market session (9:00 – 9:07 IST).
 */
export function isPreMarket(date: Date): boolean {
  return getMarketSession(date) === 'PRE_OPEN';
}

/**
 * Returns true when the market has closed for the day (after 15:30 IST on weekdays).
 * In this state, indices should display the last closing price.
 */
export function isAfterMarketClose(date: Date): boolean {
  const utc = date.getTime() + date.getTimezoneOffset() * 60000;
  const ist = new Date(utc + 5.5 * 3600000);
  const day = ist.getDay();
  if (day === 0 || day === 6) return true; // weekends always show last close
  const totalSec = ist.getHours() * 3600 + ist.getMinutes() * 60 + ist.getSeconds();
  const T_OPEN_END = 15 * 3600 + 30 * 60; // 15:30:00
  const T_PRE_START = 9 * 3600;            // 09:00:00
  // Before 9:00 or after 15:30 → show last close
  return totalSec < T_PRE_START || totalSec >= T_OPEN_END;
}

// ─── NSE/BSE Lot Sizes (F&O) ─────────────────────────────────────────────────
// Source: NSE circular — updated periodically

export const NSE_LOT_SIZES: Record<string, number> = {
  'NIFTY':        50,
  'NIFTY 50':     50,
  'BANKNIFTY':    15,
  'FINNIFTY':     40,
  'MIDCPNIFTY':   75,
  'GIFTNIFTY':    50,
  'SENSEX':       10,
  'RELIANCE':     250,
  'TCS':          150,
  'HDFCBANK':     550,
  'INFY':         300,
  'ICICIBANK':    700,
  'BAJFINANCE':   125,
  'WIPRO':        1500,
  'AXISBANK':     1200,
  'SUNPHARMA':    350,
  'MARUTI':       100,
  'TATASTEEL':    5500,
  'ONGC':         3850,
};

export function getLotSize(symbol: string): number {
  return NSE_LOT_SIZES[symbol.toUpperCase()] ?? 1;
}

// ─── Circuit Limits ───────────────────────────────────────────────────────────
// SEBI mandated price bands

export type CircuitBand = '2%' | '5%' | '10%' | '20%' | 'No Band';

export const CIRCUIT_BANDS: Record<string, CircuitBand> = {
  'NIFTY 50':   'No Band',
  'BANKNIFTY':  'No Band',
  'FINNIFTY':   'No Band',
  'MIDCPNIFTY': 'No Band',
  'SENSEX':     'No Band',
  'GIFTNIFTY':  'No Band',
};

export function getCircuitBand(symbol: string): CircuitBand {
  return CIRCUIT_BANDS[symbol.toUpperCase()] ?? '20%';
}

export function getCircuitLimits(ltp: number, band: CircuitBand): { upper: number; lower: number } | null {
  if (band === 'No Band') return null;
  const pct = parseFloat(band) / 100;
  return {
    upper: parseFloat((ltp * (1 + pct)).toFixed(2)),
    lower: parseFloat((ltp * (1 - pct)).toFixed(2)),
  };
}

// ─── T+1 Settlement ───────────────────────────────────────────────────────────

/** Returns settlement date (T+1 for equities, skipping weekends & holidays) */
export function getSettlementDate(tradeDate: Date): Date {
  const d = new Date(tradeDate);
  let added = 0;
  while (added < 1) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added++;
  }
  return d;
}

// ─── Indian Tax Calculations ──────────────────────────────────────────────────

export interface TaxBreakdown {
  stt: number;           // Securities Transaction Tax
  gst: number;           // GST on brokerage (18%)
  stampDuty: number;     // Stamp duty (buy side only)
  exchangeTxnCharge: number;
  sebiTurnoverFee: number;
  brokerage: number;
  totalCharges: number;
  netAmount: number;
}

export interface TaxInput {
  side: 'BUY' | 'SELL';
  segment: 'EQ' | 'FO' | 'INTRADAY';
  qty: number;
  price: number;
  brokerage?: number; // default ₹20 flat
}

/**
 * Calculate all Indian market charges per SEBI/NSE/BSE rules
 * Rates as of FY 2025-26
 */
export function calculateCharges(input: TaxInput): TaxBreakdown {
  const { side, segment, qty, price, brokerage: brokerageInput = 20 } = input;
  const turnover = qty * price;

  // STT rates (SEBI mandated)
  let sttRate = 0;
  if (segment === 'EQ') {
    sttRate = side === 'BUY' ? 0.001 : 0.001;   // 0.1% on both sides for delivery
  } else if (segment === 'INTRADAY') {
    sttRate = side === 'SELL' ? 0.00025 : 0;     // 0.025% on sell side only
  } else if (segment === 'FO') {
    sttRate = side === 'SELL' ? 0.0005 : 0;      // 0.05% on sell side (options premium)
  }
  const stt = parseFloat((turnover * sttRate).toFixed(2));

  // Exchange transaction charges (NSE)
  let exchRate = 0;
  if (segment === 'EQ' || segment === 'INTRADAY') exchRate = 0.0000297;
  else if (segment === 'FO') exchRate = 0.0000495;
  const exchangeTxnCharge = parseFloat((turnover * exchRate).toFixed(2));

  // SEBI turnover fee: ₹10 per crore
  const sebiTurnoverFee = parseFloat((turnover * 0.000001).toFixed(4));

  // Stamp duty (buy side only, as per Finance Act 2020)
  let stampRate = 0;
  if (side === 'BUY') {
    if (segment === 'EQ') stampRate = 0.00015;          // 0.015%
    else if (segment === 'INTRADAY') stampRate = 0.00003; // 0.003%
    else if (segment === 'FO') stampRate = 0.00003;       // 0.003%
  }
  const stampDuty = parseFloat((turnover * stampRate).toFixed(2));

  // Brokerage (flat ₹20 or 0.03% whichever is lower — typical discount broker)
  const brokerage = Math.min(brokerageInput, turnover * 0.0003);

  // GST on brokerage + exchange charges (18%)
  const gst = parseFloat(((brokerage + exchangeTxnCharge) * 0.18).toFixed(2));

  const totalCharges = parseFloat((stt + gst + stampDuty + exchangeTxnCharge + sebiTurnoverFee + brokerage).toFixed(2));
  const netAmount = side === 'BUY'
    ? parseFloat((turnover + totalCharges).toFixed(2))
    : parseFloat((turnover - totalCharges).toFixed(2));

  return { stt, gst, stampDuty, exchangeTxnCharge, sebiTurnoverFee, brokerage, totalCharges, netAmount };
}

// ─── LTCG / STCG ─────────────────────────────────────────────────────────────

export interface CapitalGainsTax {
  holdingPeriod: 'LTCG' | 'STCG';
  taxRate: number;
  taxableGain: number;
  taxAmount: number;
  exemptionApplied: number;
  note: string;
}

/**
 * Calculate LTCG/STCG as per Finance Act 2024 (Budget 2024 changes)
 * LTCG: >12 months, 12.5% (no indexation), ₹1.25L exemption
 * STCG: ≤12 months, 20% (equity/MF via STT)
 */
export function calculateCapitalGains(
  buyPrice: number,
  sellPrice: number,
  qty: number,
  holdingDays: number
): CapitalGainsTax {
  const gain = (sellPrice - buyPrice) * qty;
  const isLTCG = holdingDays > 365;

  if (isLTCG) {
    const exemption = Math.min(gain, 125000); // ₹1.25L exemption
    const taxableGain = Math.max(0, gain - exemption);
    return {
      holdingPeriod: 'LTCG',
      taxRate: 12.5,
      taxableGain,
      taxAmount: parseFloat((taxableGain * 0.125).toFixed(2)),
      exemptionApplied: exemption,
      note: 'LTCG @ 12.5% (Budget 2024). ₹1.25L annual exemption applied.',
    };
  } else {
    return {
      holdingPeriod: 'STCG',
      taxRate: 20,
      taxableGain: gain,
      taxAmount: parseFloat((gain * 0.20).toFixed(2)),
      exemptionApplied: 0,
      note: 'STCG @ 20% on equity/ETF sold within 12 months (Budget 2024).',
    };
  }
}

// ─── SEBI Risk Disclosures ────────────────────────────────────────────────────

export const SEBI_RISK_DISCLOSURES = [
  'Investments in securities market are subject to market risks. Read all the related documents carefully before investing.',
  '70% of individual traders in equity F&O segment incurred net losses during FY 2021-22 (SEBI study).',
  'Past performance is not indicative of future results.',
  'Options trading involves significant risk and is not suitable for all investors.',
  'Derivatives are leveraged instruments — losses can exceed initial investment.',
  'This platform is for informational purposes only and does not constitute investment advice.',
];

export const SEBI_REGISTRATION = 'SEBI Registration: INZ000000000 | NSE: CM/FO/CD | BSE: CM/FO | CDSL: IN-DP-000-2024';

export const INVESTOR_GRIEVANCE = 'Investor Grievance: grievance@mytrading.in | SEBI SCORES: https://scores.sebi.gov.in';
